import React, { useState, useEffect } from 'react';
import STR1 from './STR1';
import STR2 from './STR2';
import STR3 from './STR3';
import STR4 from './STR4';
import STR5 from './STR5';
import STR6 from './STR6';
import { useNavigate } from 'react-router-dom';

const StringerMain = () => {
    const [activeTab, setActiveTab] = useState('STR #1');
    const navigate = useNavigate();

    // Global fields for shared data across tabs
    const [globalFields, setGlobalFields] = useState({
        date: '',
        shift: '',
        inspectorName: '',
    });

    // Form data for each tab
    const [formData, setFormData] = useState(() => {
        const savedData = sessionStorage.getItem('stringerMainFormData');
        return savedData ? JSON.parse(savedData) : {
            'STR #1': {},
            'STR #2': {},
            'STR #3': {},
            'STR #4': {},
            'STR #5': {},
            'STR #6': {},
        };
    });

    // Persist form data in sessionStorage whenever it changes
    useEffect(() => {
        sessionStorage.setItem('stringerMainFormData', JSON.stringify(formData));
    }, [formData]);

    // Initialize global fields from session storage
    useEffect(() => {
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');

        setGlobalFields((prevFields) => ({
            ...prevFields,
            date: inspectionDate || '',
            shift: inspectionShift || '',
            inspectorName: inspectorName || '', // Update inspectorName with username from context
        }));
    }, []); // Run once on component mount

    // Handle changes to global fields
    const handleGlobalFieldChange = (e) => {
        const { name, value } = e.target;
        setGlobalFields((prevFields) => ({
            ...prevFields,
            [name]: value,
        }));
    };

    // Handle changes to form data for a specific tab
    const handleFormDataChange = (tab, data) => {
        setFormData((prevData) => ({
            ...prevData,
            [tab]: {
                ...prevData[tab],
                ...data,
            },
        }));
    };

    // Navigation functions
    const goToNextForm = () => {
        navigate('/inspections/section2/form6');
    };

    const goToPreviousForm = () => {
        navigate('/inspections/section2/form4');
    };

    // Render the active tab content
    const renderActiveTab = () => {
        const tabProps = {
            globalFields,
            formData: formData[activeTab],
            onFormDataChange: (data) => handleFormDataChange(activeTab, data),
        };

        switch (activeTab) {
            case 'STR #1':
                return <STR1 {...tabProps} />;
            case 'STR #2':
                return <STR2 {...tabProps} />;
            case 'STR #3':
                return <STR3 {...tabProps} />;
            case 'STR #4':
                return <STR4 {...tabProps} />;
            case 'STR #5':
                return <STR5 {...tabProps} />;
            case 'STR #6':
                return <STR6 {...tabProps} />;
            default:
                return <STR1 {...tabProps} />;
        }
    };

    // List of tabs
    const tabItems = [
        'STR #1',
        'STR #2',
        'STR #3',
        'STR #4',
        'STR #5',
        'STR #6',
    ];

    return (
        <div className="flex max-w-7xl mx-auto bg-white shadow-lg rounded-lg overflow-hidden">
            {/* Sidebar for Tabs */}
            <div className="w-1/4 bg-[#424752] text-white p-4">
                <h3 className="text-lg font-semibold mb-4">Stringer Machine Parameters Verification</h3>
                <div className="space-y-2">
                    {tabItems.map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`w-full text-gray-300 text-left p-3 rounded-lg transition-colors duration-300 ${
                                activeTab === tab
                                    ? 'text-gray-500 bg-white font-bold'
                                    : 'hover:bg-gray-200 hover:text-black'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <div className="w-3/4 p-6">
                {/* Global Fields */}
                <div className="bg-gray-200 p-4 rounded-lg mb-6">
                    <h3 className="text-lg font-semibold mb-3 text-gray-700">Global Fields</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-gray-700 ml-1 font-semibold">Date</label>
                            <input
                                type="date"
                                name="date"
                                value={globalFields.date}
                                onChange={handleGlobalFieldChange}
                                className="border p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                                readOnly
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 ml-1 font-semibold">Shift</label>
                            <input
                                type="text"
                                name="shift"
                                placeholder="Shift"
                                value={globalFields.shift}
                                onChange={handleGlobalFieldChange}
                                className="border p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                                readOnly
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 ml-1 font-semibold">Inspector Name</label>
                            <input
                                type="text"
                                name="inspectorName"
                                placeholder="Inspector Name"
                                value={globalFields.inspectorName}
                                onChange={handleGlobalFieldChange}
                                className="border p-3 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                                readOnly
                            />
                        </div>
                    </div>
                </div>

                {/* Active Tab Content */}
                <div className="bg-gray-50 p-6 rounded-lg shadow-inner">
                    {renderActiveTab()}
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-6">
                    <button
                        onClick={goToPreviousForm}
                        className="bg-[#424752] text-white w-[6rem] px-4 py-2 rounded-lg hover:bg-gray-500"
                    >
                        Previous
                    </button>
                    <button
                        onClick={goToNextForm}
                        className="bg-[#424752] text-white w-[6rem] px-4 py-2 rounded-lg hover:bg-gray-500"
                    >
                        Next
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StringerMain;